import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import {
    useAppDispatch,
    useAppSelector,
    ticketInfoSelector,
    sentMessageSelector,
    editingMessageSelector,
    setTicketInfo,
    setTicketPageNumber,
    setSentMessage,
    setEditingMessage,
    useTicketHappinessMutation,
} from '../../../app/store';

import {
    TicketMessageType,
    TicketInfoType,
} from '../ticket.types';
import { immediateToast } from 'utils/toast';
import ticketApi from '../api/tickets.api';

const useTicketActions = () => {
    const { ticketId } = useParams();
    const dispatch = useAppDispatch();
    const ticketInfo = useAppSelector(ticketInfoSelector);
    const sentMessage = useAppSelector(sentMessageSelector);
    const editingMessage = useAppSelector(editingMessageSelector);
    const [ticketHappiness] = useTicketHappinessMutation();

    const handleEditMessage = useCallback(
        (message: TicketMessageType) => {
            if (!editingMessage) {
                const lastMessage = ticketInfo?.message?.items[0];
                dispatch(setSentMessage(message ? message : lastMessage));
                dispatch(setEditingMessage(message ? message : lastMessage));
            }
        },
        [ticketInfo, editingMessage],
    );

    const handleCancelEditMessage = useCallback(() => {
        if (!ticketInfo?.message) return ticketInfo;

        const updatedItems = ticketInfo.message.items
            .filter((msg) => msg.id !== 0)
            .map((msg) =>
                msg.id === editingMessage?.id ? editingMessage : msg,
            );

        dispatch(
            setTicketInfo({
                ...ticketInfo,
                message: { ...ticketInfo.message, items: updatedItems },
            }),
        );

        dispatch(setSentMessage(undefined));
        dispatch(setEditingMessage(undefined));
    }, [ticketInfo, editingMessage]);

    const handleCancelMessage = useCallback(() => {
        if (!ticketInfo || !ticketInfo.message || !ticketInfo.message.items) {
            return ticketInfo;
        }

        const { message } = ticketInfo;
        let updatedMessageItems = [...message?.items];

        if (editingMessage && sentMessage?.id !== 0) {
            // update last message with server data
            updatedMessageItems = updatedMessageItems.map((item) =>
                item.id === sentMessage?.id ? editingMessage : item,
            );
        } else {
            updatedMessageItems.splice(0, 1);
        }

        dispatch(
            setTicketInfo({
                ...ticketInfo,
                message: {
                    ...message,
                    items: updatedMessageItems,
                },
            }),
        );

        dispatch(setSentMessage(undefined));
        dispatch(setEditingMessage(undefined));
    }, [ticketInfo, editingMessage, sentMessage]);

    const handlePaginateMessages = useCallback((ticketPageNumber: number) => {
        dispatch(setTicketPageNumber(ticketPageNumber));
    }, []);

    const handleSatisfaction = async (mood: 'happy' | 'unhappy') => {
        if (!ticketId || ticketInfo?.status !== 'closed') return;
        if (ticketInfo?.satisfaction) return;

        try {
            await ticketHappiness({
                id: Number(ticketId),
                satisfaction: mood,
            }).unwrap();

            dispatch(
                setTicketInfo({
                    ...(ticketInfo as TicketInfoType),
                    satisfaction: mood,
                }),
            );

            // Keep RTK Query's cached detail in sync so reopening the ticket
            // cannot make a submitted satisfaction look editable again.
            dispatch(
                ticketApi.util.updateQueryData(
                    'getTicket',
                    Number(ticketId),
                    (draft) => {
                        draft.satisfaction = mood;
                    },
                ),
            );
        } catch (error: any) {
            immediateToast('error', {
                message: error?.data?.message ?? 'ثبت نظر شما انجام نشد.',
                timeout: 3000,
            });
            throw error;
        }
    };

    return {
        onEditMessage: handleEditMessage,
        onCancelEditMessage: handleCancelEditMessage,
        onCancelMessage: handleCancelMessage,
        onPaginateMessages: handlePaginateMessages,
        onSatisfaction: handleSatisfaction,
    };
};

export default useTicketActions;
